/**
 * Detail-card action helpers — sharing and order request modals.
 *
 * The mockup wires `#btnShare` directly inline; this module isolates the
 * logic so estate-detail.ts can stay focused on rendering. Both flows use
 * the lightweight prompt/confirm pattern (no extra DOM overlay) — keeps the
 * implementation small and accessible. A richer modal can land in M7 if UX
 * feedback asks for it.
 */
import { createShare } from '../api/shares.ts';
import { createOrder } from '../api/orders.ts';
import { createSavedEstate } from '../api/saved-estates.ts';
import { currentUser } from '../state/session.ts';
import { t } from '../i18n/index.ts';
import { APIRequestError } from '../api/client.ts';

/** Bind the "Jaa arvio" button on the detail card. Opens a small modal to
 *  collect recipient email + optional message and posts to /smt/shares. */
export function bindShareButton(estateID: number, estateName: string): void {
  const btn = document.getElementById('btnShare') as HTMLButtonElement | null;
  if (!btn) return;
  btn.addEventListener('click', () => void openShareFlow(estateID, estateName));
}

async function openShareFlow(estateID: number, estateName: string): Promise<void> {
  const u = currentUser();
  if (!u) {
    alert(t('share_login_required'));
    return;
  }
  const email = prompt(t('share_recipient_prompt', { estate: estateName }));
  if (!email) return;
  const message = prompt(t('share_message_prompt')) ?? '';
  try {
    await createShare({ estate_id: estateID, recipient_email: email.trim(), message: message.trim() || undefined });
    alert(t('share_success'));
  } catch (e) {
    alert(formatErr(e as Error));
  }
}

/** Bind a "Pyydä arviota" button by id. Used on the detail card for
 *  level=1 users browsing estates outside their allowed_regions, and as a
 *  generic escalation channel otherwise. */
export function bindOrderButton(buttonID: string, estateID: number, estateName: string): void {
  const btn = document.getElementById(buttonID) as HTMLButtonElement | null;
  if (!btn) return;
  btn.addEventListener('click', () => void openOrderFlow(estateID, estateName));
}

async function openOrderFlow(estateID: number, estateName: string): Promise<void> {
  const u = currentUser();
  if (!u) {
    alert(t('order_login_required'));
    return;
  }
  const message = prompt(t('order_message_prompt', { estate: estateName })) ?? '';
  try {
    await createOrder({ estate_id: estateID, message: message.trim() || undefined });
    alert(t('order_success'));
  } catch (e) {
    alert(formatErr(e as Error));
  }
}

/** Bind the "Tallenna arvio" button on the detail card (M-FB1 / audit v2
 *  virhe 1). Posts to /smt/saved-estates and shows a small confirmation
 *  alert; the backend endpoint is idempotent so re-clicking does not
 *  produce duplicates. Used by Taso 2 / Pro / Taso 3 callers — see
 *  primaryActionButton() in views/estate-detail.ts. */
export function bindSaveButton(estateID: number, estateName: string): void {
  const btn = document.getElementById('btnSave') as HTMLButtonElement | null;
  if (!btn) return;
  btn.addEventListener('click', () => void openSaveFlow(btn, estateID, estateName));
}

async function openSaveFlow(btn: HTMLButtonElement, estateID: number, estateName: string): Promise<void> {
  const u = currentUser();
  if (!u) {
    alert(t('save_login_required'));
    return;
  }
  // Optional muistiinpano — empty input is fine and stored as NULL.
  const note = prompt(t('save_note_prompt', { estate: estateName })) ?? '';
  btn.disabled = true;
  try {
    await createSavedEstate({ estate_id: estateID, note: note.trim() || undefined });
    btn.textContent = t('save_btn_saved');
    alert(t('save_success'));
  } catch (e) {
    alert(formatErr(e as Error));
    btn.disabled = false;
  }
}

function formatErr(e: Error): string {
  if (e instanceof APIRequestError) return e.body.error || e.message;
  return e.message;
}
